/**
 * Wizard - Felles abstrakt wizard-komponent
 * Gjenbrukbar stegveiviser for hele RettVerk-plattformen
 * @version 1.0.0
 * 
 * Brukes av:
 * - KontraktWizard (arbeidsrett)
 * - OppsigelseWizard (arbeidsrett)
 * - PermitteringsWizard (arbeidsrett)
 * - FKUExportWizard (forbrukerrett)
 * - LeiekontraktGenerator (husleierett)
 */

import React, { useState, useCallback, Fragment } from 'react';
import {
  ChevronRight, ChevronLeft, CheckCircle,
  Loader2, HardDrive, FolderOpen
} from 'lucide-react';

/**
 * WizardHeader - Viser fremdrift og lagringstilstand
 */
export function WizardHeader({
  title,
  subtitle,
  icon: Icon,
  iconColor = 'var(--copper)',
  steps,
  activeStep,
  onStepClick,
  libraryReady,
  onSetupLibrary,
  showLibrary = true,
  activeColor = 'var(--copper)'
}) {
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {Icon && (
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${iconColor}20` }}
            >
              <Icon className="w-5 h-5" style={{ color: iconColor }} />
            </div>
          )}
          <div>
            <h2 
              className="text-lg font-bold text-[var(--text-main)]" 
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {title}
            </h2>
            {subtitle && (
              <p className="text-xs text-[var(--text-muted)]">{subtitle}</p>
            )}
          </div>
        </div>
        
        {showLibrary && (
          <>
            {!libraryReady ? (
              <button
                onClick={onSetupLibrary}
                className="flex items-center gap-2 px-3 py-1.5 bg-[var(--copper)]/10 text-[var(--copper)] rounded-lg text-sm hover:bg-[var(--copper)]/20"
              >
                <FolderOpen className="w-4 h-4" />
                Velg lagringsmappe
              </button>
            ) : (
              <div className="flex items-center gap-1 px-2 py-1 rounded bg-[var(--success)]/10 border border-[var(--success)]/30">
                <HardDrive className="w-3 h-3 text-[var(--success)]" />
                <span className="text-xs text-[var(--success)]">Lokal lagring</span>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Steg-indikator */}
      <WizardStepIndicator
        steps={steps}
        activeStep={activeStep}
        onStepClick={onStepClick}
        activeColor={activeColor}
      />
    </div>
  );
}

/**
 * WizardStepIndicator - Klikkbar stegnavigasjon
 */
export function WizardStepIndicator({
  steps,
  activeStep,
  onStepClick,
  activeColor = 'var(--copper)'
}) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2">
      {steps.map((step, idx) => {
        const StepIcon = step.icon;
        const isActive = step.id === activeStep || idx + 1 === activeStep;
        const isCompleted = typeof activeStep === 'number' 
          ? idx + 1 < activeStep 
          : steps.findIndex(s => s.id === activeStep) > idx;
        const stepId = step.id || idx + 1;
        
        return (
          <Fragment key={stepId}>
            <button
              onClick={() => onStepClick?.(stepId)}
              disabled={!isCompleted && !isActive}
              data-testid={`wizard-step-${stepId}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                isActive
                  ? 'text-white'
                  : isCompleted
                  ? 'bg-[var(--success)]/10 text-[var(--success)]'
                  : 'bg-[var(--bg-highlight)] text-[var(--text-muted)]'
              }`}
              style={isActive ? { backgroundColor: activeColor } : {}}
            >
              {isCompleted ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                StepIcon && <StepIcon className="w-4 h-4" />
              )}
              <span className="hidden md:inline">{step.tittel || step.label}</span>
            </button>
            {idx < steps.length - 1 && (
              <ChevronRight className="w-4 h-4 text-[var(--text-muted)] flex-shrink-0" />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

/**
 * WizardContent - Innholdsbeholder for hvert steg
 */
export function WizardContent({ children, className = '' }) {
  return (
    <div className={`bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded-lg p-6 ${className}`}>
      {children}
    </div>
  );
}

/**
 * WizardNavigation - Navigasjonsknapper nederst
 */
export function WizardNavigation({
  activeStep,
  totalSteps,
  onNext,
  onPrevious,
  onSave,
  canGoNext = true,
  canGoPrevious = true,
  saving = false,
  showSave = false,
  nextLabel = 'Neste',
  previousLabel = 'Forrige',
  saveLabel = 'Lagre',
  activeColor = 'var(--copper)',
  saveDisabled = false
}) {
  const isFirstStep = activeStep === 1;
  const isLastStep = activeStep === totalSteps;
  
  return (
    <div className="flex items-center justify-between">
      <button
        onClick={onPrevious}
        disabled={isFirstStep || !canGoPrevious}
        data-testid="wizard-prev-btn"
        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium ${
          isFirstStep || !canGoPrevious
            ? 'bg-[var(--bg-highlight)] text-[var(--text-muted)] cursor-not-allowed'
            : 'bg-[var(--bg-highlight)] text-[var(--text-main)] hover:bg-[var(--bg-card)]'
        }`}
      >
        <ChevronLeft className="w-4 h-4" />
        {previousLabel}
      </button>

      <div className="flex items-center gap-3">
        {showSave && isLastStep && (
          <button
            onClick={onSave}
            disabled={saving || saveDisabled}
            data-testid="wizard-save-btn"
            className="flex items-center gap-2 px-4 py-2 bg-[var(--success)] text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <HardDrive className="w-4 h-4" />
            )}
            {saveLabel}
          </button>
        )}
        
        {!isLastStep && (
          <button
            onClick={onNext}
            disabled={!canGoNext}
            data-testid="wizard-next-btn"
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: activeColor }}
          >
            {nextLabel}
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * useWizard - Hook for wizard state-håndtering
 */
export function useWizard(totalSteps, initialStep = 1) {
  const [activeStep, setActiveStep] = useState(initialStep);
  const [saving, setSaving] = useState(false);
  
  const next = useCallback(() => {
    if (activeStep < totalSteps) {
      setActiveStep(prev => prev + 1);
    }
  }, [activeStep, totalSteps]);
  
  const previous = useCallback(() => {
    if (activeStep > 1) {
      setActiveStep(prev => prev - 1);
    }
  }, [activeStep]);
  
  const goToStep = useCallback((step) => {
    if (step >= 1 && step <= totalSteps) {
      setActiveStep(step);
    }
  }, [totalSteps]);
  
  const reset = useCallback(() => {
    setActiveStep(initialStep);
    setSaving(false);
  }, [initialStep]);
  
  return {
    activeStep,
    setActiveStep,
    next,
    previous,
    goToStep,
    reset,
    saving,
    setSaving,
    isFirstStep: activeStep === 1,
    isLastStep: activeStep === totalSteps
  };
}

/**
 * Wizard - Komplett wizard-komponent som kombinerer alle deler
 * 
 * @example
 * const STEG = [
 *   { id: 1, tittel: 'Steg 1', icon: Building2 },
 *   { id: 2, tittel: 'Steg 2', icon: User },
 * ];
 * 
 * <Wizard
 *   title="Min Wizard"
 *   steps={STEG}
 *   libraryReady={true}
 *   onSave={handleSave}
 * >
 *   {(activeStep) => (
 *     <>
 *       {activeStep === 1 && <Steg1 />}
 *       {activeStep === 2 && <Steg2 />}
 *     </>
 *   )}
 * </Wizard>
 */
export function Wizard({
  // Header props
  title,
  subtitle,
  icon,
  iconColor,
  activeColor = 'var(--copper)',
  
  // Steps
  steps,
  initialStep = 1,
  
  // Library/storage
  showLibrary = true,
  libraryReady,
  onSetupLibrary,
  
  // Actions
  onSave,
  onComplete,
  
  // Navigation control
  canGoNext,
  saveDisabled,
  
  // Labels
  nextLabel,
  previousLabel,
  saveLabel,
  
  // Content
  children,
  className = ''
}) {
  const wizard = useWizard(steps.length, initialStep);
  
  const handleNext = useCallback(() => {
    wizard.next();
  }, [wizard]);
  
  const handlePrevious = useCallback(() => {
    wizard.previous();
  }, [wizard]);
  
  const handleStepClick = useCallback((stepId) => {
    const stepIndex = typeof stepId === 'number' 
      ? stepId 
      : steps.findIndex(s => s.id === stepId) + 1;
    
    if (stepIndex <= wizard.activeStep) {
      wizard.goToStep(stepIndex);
    }
  }, [steps, wizard]);
  
  const handleSave = useCallback(async () => {
    if (!onSave) return;
    
    wizard.setSaving(true);
    try {
      await onSave();
      onComplete?.();
    } finally {
      wizard.setSaving(false);
    }
  }, [onSave, onComplete, wizard]);
  
  return (
    <div className={`space-y-6 ${className}`} data-testid="wizard-container">
      <WizardHeader
        title={title}
        subtitle={subtitle}
        icon={icon}
        iconColor={iconColor}
        steps={steps}
        activeStep={wizard.activeStep}
        onStepClick={handleStepClick}
        libraryReady={libraryReady}
        onSetupLibrary={onSetupLibrary}
        showLibrary={showLibrary}
        activeColor={activeColor}
      />
      
      <WizardContent>
        {typeof children === 'function' 
          ? children(wizard.activeStep, wizard) 
          : children
        }
      </WizardContent>
      
      <WizardNavigation
        activeStep={wizard.activeStep}
        totalSteps={steps.length}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSave={handleSave}
        canGoNext={typeof canGoNext === 'function' ? canGoNext(wizard.activeStep) : canGoNext}
        saving={wizard.saving}
        showSave={!!onSave}
        saveDisabled={saveDisabled}
        nextLabel={nextLabel}
        previousLabel={previousLabel}
        saveLabel={saveLabel}
        activeColor={activeColor}
      />
    </div>
  );
}

// Eksporter alle komponenter og hook
export default Wizard;
